import { isDefined } from "@clipboard-health/util-ts";

interface GetScoreSubtitleParams {
  wasLate: boolean;
  latenessMinutes?: number;
  didLosePointsForTimeliness: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  isFirstShiftInDouble: boolean;
  isSecondShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  formattedTargetArrivalTime?: string;
}

const DOUBLE_SHIFT_SUFFIX =
  "\n\nThis is a double, so your next shift will not receive arrival time bonuses or penalties.";

function formatLateness(latenessMinutes?: number): string {
  return isDefined(latenessMinutes) ? `${latenessMinutes} minutes late` : "late";
}

/**
 * Determines the subtitle text for the EC clipboard score page
 * based on lateness, timeliness penalty, target arrival time, and double shift status.
 */
export function getScoreSubtitle(params: GetScoreSubtitleParams): string {
  const {
    wasLate,
    latenessMinutes,
    didLosePointsForTimeliness,
    isArrivalTimeFeatureEnabled,
    isFirstShiftInDouble,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    formattedTargetArrivalTime,
  } = params;

  if (isSecondShiftInDouble) {
    return "On-time bonuses and late penalties don't apply to the second shift in a double.";
  }

  const doubleShiftSuffix = isFirstShiftInDouble ? DOUBLE_SHIFT_SUFFIX : "";

  if (!wasLate) {
    return `You arrived on time! That helped your team and earned you bonus points.${doubleShiftSuffix}`;
  }

  // legacy short lead time booking (being replaced by TAT)
  if (wasBookedWithShortLeadTime) {
    return "Since this shift was booked within 4 hours of the start time, you didn't lose points for arriving late.";
  }

  const hasTargetArrivalTime = isArrivalTimeFeatureEnabled && isDefined(formattedTargetArrivalTime);

  const lateness = formatLateness(latenessMinutes);

  if (didLosePointsForTimeliness) {
    if (hasTargetArrivalTime) {
      return `You committed to arriving by ${formattedTargetArrivalTime} at booking, but you arrived ${lateness}.${doubleShiftSuffix}`;
    }

    return `You arrived ${lateness} to this shift.${doubleShiftSuffix}`;
  }

  if (hasTargetArrivalTime) {
    return `You committed to arriving by ${formattedTargetArrivalTime}, but you were ${lateness}. You didn't lose points, but you missed the on-time bonus!${doubleShiftSuffix}`;
  }

  return `You were ${lateness}. You didn't lose points, but you missed the on-time bonus!${doubleShiftSuffix}`;
}
